import type { FollowListType } from '~/app/(default-layout)/user/[userId]/_containers/follows-container/types';
import { convertIUserResponseToUserOverview } from '~/common/converter/user/user';
import { logError } from '~/utils/utils';
import type { FollowList } from '@app/(default-layout)/user/[userId]/_containers/follows-container/use-fetch-follow-list';
import { buildUser } from '@app/_repositories/query-builder/user-query-builder';
import { getQueriesForClient } from '@app/_repositories/query-service';
import type { UserCursorRequest } from '@app/_repositories/query-types';

export const FOLLOW_USERS_PER_PAGE = 50;

export const fetchFollowListing = async ({
  userId,
  followListType,
  cursor = '',
  limit = FOLLOW_USERS_PER_PAGE,
}: {
  userId: string;
  followListType: FollowListType;
  cursor?: string;
  limit?: number;
}): Promise<FollowList> => {
  try {
    const userFollowListRequestId = `FETCH_USER_FOLLOW-LIST-${userId}`;

    const userFollowListRequest: UserCursorRequest = {
      require_user_list: {
        ...buildUser(),
        require_searchable_story_count: true,
        require_vip_status: undefined,
        require_user_role: undefined,
        require_profile: true,
        require_followee: {
          cursor: {
            require_cursor_info: {
              require_total_count: true,
            },
          },
        },
        require_follower: {
          cursor: {
            require_cursor_info: {
              require_total_count: true,
            },
          },
        },
      },
      cursor: {
        cursor,
        limit,
        require_cursor_info: {
          require_has_next_page: true,
          require_end_cursor: true,
          require_total_count: true,
        },
      },
    };

    const userFollowListResponse = await getQueriesForClient({
      request_list: [
        {
          request_id: userFollowListRequestId,
          user_request: {
            id: userId,
            require_user: {
              [followListType === 'follower'
                ? 'requireFollower'
                : 'requireFollowee']: userFollowListRequest,
            },
          },
        },
      ],
    });

    const user = userFollowListResponse?.response_list[0]?.user;

    if (!user) {
      return {
        userList: [],
      };
    }

    const list =
      followListType === 'followee' ? user.followee_page : user.follower_page;
    return {
      userList:
        list?.user_list?.map((u) => convertIUserResponseToUserOverview(u)) ||
        [],
      nextCursor: list?.cursor?.end_cursor || '',
      hasNextPage: list?.cursor?.has_next_page || false,
    };
  } catch (error: any) {
    logError(error);
    return {
      userList: [],
    };
  }
};
